import { combineReducers } from 'redux'
import * as omit from 'lodash/omit'
import {
  BreakdownAppState,
  MoneyflowAppState,
  MoneyflowCategoriesAppState,
  TrackersAppState,
  TransactionsAppState,
  FullMoneyflowType,
} from 'interfaces/moneyflow'
import { CategoryId } from 'interfaces/meta'
import {
  GET_TRANSACTIONS,
  SET_BREAKDOWN_VISIBILITY,
  SET_CATEGORIES_VISIBILITY,
  SET_TRANSACTIONS,
  TOGGLE_MONEYFLOW_TRACKER_DETAILS,
  TOGGLE_TRANSACTION_EDIT,
  TOGGLE_TRANSACTIONS_LOADING,
} from 'actions/moneyflowActions'
import { PAGE_CHANGED, SET_FILTER_CATEGORY, SET_TIMEFRAME } from 'actions/globalActions'
import { createFilterCategoryReducer, createHighlightedCategoryReducer, createTimeframeReducer, createTrendModeReducer } from 'reducers/globalReducer'
import { Reducer } from 'interfaces'
import { PAGE_SIZE_OPTIONS } from 'constants/component'

const TRANSACTIONS_INITIAL_STATE: TransactionsAppState = {
  isLoading: false,
  page: 1,
  pageSize: PAGE_SIZE_OPTIONS[0],
  editingId: null,
  sorting: {
    columnName: 'date',
    direction: 'desc',
  },
}

const getInitialState = (flowType: FullMoneyflowType) => {
  return {
    ...TRANSACTIONS_INITIAL_STATE,
    ...flowType === 'transactions' ? { pageSize: PAGE_SIZE_OPTIONS[2] } : {},
  }
}

// TODO: split this reducer
const createTransactionsReducer = (flowType: FullMoneyflowType): Reducer<TransactionsAppState> =>
  (state = getInitialState(flowType), action) => {
    switch (action.type) {

      case TOGGLE_TRANSACTION_EDIT: {
        const { type, uid, toggleValue } = action.payload
        if (type !== flowType) {
          return state
        }

        if (toggleValue) {
          return {
            ...state,
            editingId: uid,
          }

        } else if (!toggleValue && state.editingId === uid) {
          return {
            ...state,
            editingId: null,
          }
        }

        return state
      }

      case GET_TRANSACTIONS: {
        const { type } = action.payload
        if (type !== flowType) {
          return state
        }

        return {
          ...state,
          editingId: null,
        }
      }

      case PAGE_CHANGED:
        return {
          ...state,
          editingId: null,
        }

      case TOGGLE_TRANSACTIONS_LOADING: {
        const { type, toggleValue } = action.payload
        return type === flowType ? { ...state, ...{ isLoading: toggleValue }} : state
      }

      case SET_TRANSACTIONS: {
        const { type } = action.payload
        const nextState = omit(action.payload, ['transactions'])

        return type === flowType ? { ...state, ...nextState } : state
      }

      case SET_FILTER_CATEGORY:
      case SET_TIMEFRAME:
        return action.payload.type === flowType ? { ...state, page: 1 } : state

      default:
        return state
    }
  }

const createBreakdownIsExpandedReducer = (flowType: FullMoneyflowType): Reducer<boolean> =>
  (state = false, action) => {
    if (action.type === SET_BREAKDOWN_VISIBILITY && action.payload.type === flowType) {
      return action.payload.isVisible
    }

    return state
  }

const createCategoriesIsExpandedReducer = (flowType: FullMoneyflowType): Reducer<boolean> =>
  (state = false, action) => {
    if (action.type === SET_CATEGORIES_VISIBILITY && action.payload.type === flowType) {
      return action.payload.isVisible
    }

    return state
  }

const createToggleMoneyflowTrackerDetailsReducer = (flowType: FullMoneyflowType): Reducer<CategoryId | null> =>
  (state = null, action) => {
    if (action.type === TOGGLE_MONEYFLOW_TRACKER_DETAILS && action.payload.type === flowType) {
      return action.payload.categoryId
    }

    return state
  }

export const spendingApp = combineReducers<MoneyflowAppState>({
  timeframe: createTimeframeReducer('spending'),
  transactions: createTransactionsReducer('spending'),
  trackers: combineReducers<TrackersAppState>({
    expandedCategoryId: createToggleMoneyflowTrackerDetailsReducer('spending'),
  }),
  breakdown: combineReducers<BreakdownAppState>({
    isExpanded: createBreakdownIsExpandedReducer('spending'),
  }),
  categories: combineReducers<MoneyflowCategoriesAppState>({
    selectedId: createFilterCategoryReducer('spending'),
    highlightedId: createHighlightedCategoryReducer('spending'),
    isExpanded: createCategoriesIsExpandedReducer('spending'),
  }),
  trendMode: createTrendModeReducer('spending'),
})

export const incomeApp = combineReducers<MoneyflowAppState>({
  timeframe: createTimeframeReducer('income'),
  transactions: createTransactionsReducer('income'),
  trackers: combineReducers<TrackersAppState>({
    expandedCategoryId: createToggleMoneyflowTrackerDetailsReducer('income'),
  }),
  breakdown: combineReducers<BreakdownAppState>({
    isExpanded: createBreakdownIsExpandedReducer('income'),
  }),
  categories: combineReducers<MoneyflowCategoriesAppState>({
    selectedId: createFilterCategoryReducer('income'),
    highlightedId: createHighlightedCategoryReducer('income'),
    isExpanded: createCategoriesIsExpandedReducer('income'),
  }),
  trendMode: createTrendModeReducer('income'),
})

export const transactionsApp = combineReducers<MoneyflowAppState>({
  timeframe: createTimeframeReducer('transactions'),
  transactions: createTransactionsReducer('transactions'),
  trackers: combineReducers<TrackersAppState>({
    expandedCategoryId: createToggleMoneyflowTrackerDetailsReducer('transactions'),
  }),
  breakdown: combineReducers<BreakdownAppState>({
    isExpanded: createBreakdownIsExpandedReducer('transactions'),
  }),
  categories: combineReducers<MoneyflowCategoriesAppState>({
    selectedId: createFilterCategoryReducer('transactions'),
    highlightedId: createHighlightedCategoryReducer('transactions'),
    isExpanded: createCategoriesIsExpandedReducer('transactions'),
  }),
  trendMode: createTrendModeReducer('transactions'),
})
